import {
  Component,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import type { User as FirebaseUser } from 'firebase/auth';
import { AuthService } from '@core/services/auth.service';
import { UserService } from '@core/services/user.service';
import { VERIFICATION_MESSAGE_STATE_KEY } from '@core/utils/verification-required';
import { LoggingService } from '@core/services/logging.service';
import { FirebaseInjectionHelperService } from '@core/services/firebase-injection-helper.service';

/** Seconds the resend button stays disabled after sending a verification email. */
const RESEND_COOLDOWN_SECONDS = 30;

/**
 * VerifyEmailComponent
 *
 * Post-signup / post-login holding page for email+password users whose
 * Firebase account has not yet verified its email address. Shows the
 * signed-in email, lets the user resend the verification email (with a
 * cooldown to avoid spamming), and re-checks verification on demand.
 *
 * The "continue" action calls user.reload() followed by
 * user.getIdToken(true) — the forced token refresh is critical so that
 * Firestore security rules and backend token checks see
 * email_verified=true immediately after verification.
 */
@Component({
  selector: 'app-verify-email',
  templateUrl: './verify-email.component.html',
  styleUrl: './verify-email.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VerifyEmailComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private loggingService = inject(LoggingService);
  private firebaseHelper = inject(FirebaseInjectionHelperService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  /** Email address of the signed-in user (shown in the instructions). */
  email: string | null = null;
  /** Seconds remaining before the resend button re-enables (0 = enabled). */
  resendCooldown = 0;
  /** True while the "continue" verification re-check is running. */
  checking = false;
  /** Positive feedback (e.g. "verification email sent"). */
  infoMessage: string | null = null;
  /** Gentle warning (e.g. "not verified yet"). */
  warningMessage: string | null = null;
  /**
   * Why the user was sent here, when they arrived from a refused write rather
   * than from sign-in. The signup pages set `serverMessage` and then navigate,
   * which discards it — this page has to carry the explanation or the promise
   * of one is empty (#512).
   */
  reasonMessage: string | null = null;

  private cooldownTimer: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    // Picked up from navigation state, which survives the redirect that the
    // sending component's own serverMessage does not.
    // getCurrentNavigation() returns null once the navigation has settled (and
    // is absent from router doubles), so history.state is the fallback rather
    // than an afterthought.
    const navState =
      this.router.getCurrentNavigation?.()?.extras?.state ??
      (isPlatformBrowser(this.platformId) ? window.history.state : null);
    const reason = navState?.[VERIFICATION_MESSAGE_STATE_KEY];
    if (typeof reason === 'string' && reason) {
      this.reasonMessage = reason;
    }

    // SSR safety: Firebase Auth state is only available in the browser.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    void this.loadCurrentUser();
  }

  ngOnDestroy(): void {
    this.clearCooldownTimer();
  }

  /**
   * Resolves the signed-in Firebase user once auth state is ready.
   * Redirects to the login page when no session exists, or to the
   * post-login flow when the email is already verified.
   */
  private async loadCurrentUser(): Promise<void> {
    await this.authService.waitForAuthReady(10000);
    const firebaseUser = this.getFirebaseUser();

    if (!firebaseUser) {
      await this.router.navigate(['/login'], { replaceUrl: true });
      return;
    }

    if (firebaseUser.emailVerified) {
      await this.router.navigate(['/logging-in'], { replaceUrl: true });
      return;
    }

    this.email = firebaseUser.email;
  }

  /**
   * Resends the verification email to the signed-in user and starts the
   * resend cooldown so the button cannot be spammed.
   */
  async onResend(): Promise<void> {
    if (this.resendCooldown > 0) {
      return;
    }

    const firebaseUser = this.getFirebaseUser();
    if (!firebaseUser) {
      this.warningMessage =
        'Your session has expired. Please sign in again to resend the email.';
      return;
    }

    this.warningMessage = null;
    this.infoMessage = null;
    // Only claim success and start the cooldown if the send actually worked.
    const sent = await this.userService.sendVerificationEmail(firebaseUser);
    if (sent) {
      this.infoMessage =
        'Verification email sent. Please check your inbox and spam folder.';
      this.startCooldown();
    } else {
      this.warningMessage =
        'We could not send the verification email right now. Please try again in a moment.';
    }
  }

  /**
   * Re-checks the verification state: reloads the Firebase user, then
   * forces an ID token refresh so Firestore rules and backends see
   * email_verified=true, and finally routes into the normal post-login
   * flow when verified.
   */
  async onContinue(): Promise<void> {
    const firebaseUser = this.getFirebaseUser();
    if (!firebaseUser) {
      await this.router.navigate(['/login'], { replaceUrl: true });
      return;
    }

    this.checking = true;
    this.infoMessage = null;
    this.warningMessage = null;

    try {
      await firebaseUser.reload();
      // CRITICAL: force a token refresh so the new email_verified claim is
      // present in the ID token used by Firestore rules and API calls.
      await firebaseUser.getIdToken(true);

      if (firebaseUser.emailVerified) {
        await this.router.navigate(['/logging-in'], { replaceUrl: true });
        return;
      }

      this.warningMessage =
        'Your email is not verified yet. Please click the link in the email we sent you, then try again.';
    } catch (error) {
      this.loggingService.logError('Email verification re-check failed', {
        error,
      });
      this.warningMessage =
        'We could not confirm your verification status. Please try again in a moment.';
    } finally {
      this.checking = false;
    }
  }

  /**
   * Signs the user out via the existing logout flow and returns to login.
   */
  async onSignOut(): Promise<void> {
    await this.authService.signOutUser();
    await this.router.navigate(['/login'], { replaceUrl: true });
  }

  /**
   * Returns the raw Firebase Auth user (null during SSR or when signed out).
   */
  private getFirebaseUser(): FirebaseUser | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    return this.firebaseHelper.getAuth().currentUser;
  }

  /**
   * Starts the 30 second resend cooldown countdown (browser only).
   */
  private startCooldown(): void {
    this.resendCooldown = RESEND_COOLDOWN_SECONDS;
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.clearCooldownTimer();
    this.cooldownTimer = setInterval(() => {
      this.resendCooldown -= 1;
      if (this.resendCooldown <= 0) {
        this.resendCooldown = 0;
        this.clearCooldownTimer();
      }
    }, 1000);
  }

  /**
   * Stops the cooldown countdown timer if it is running.
   */
  private clearCooldownTimer(): void {
    if (this.cooldownTimer !== null) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
  }
}
